import { useMemo } from 'react';
import Select from '@atlaskit/select';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import PageHeader from 'shared/components/page-header/page-header';
import SharedButton from 'shared/components/buttons/button';
import SharedSegmentedControl from 'shared/components/buttons/segmented-control/segmented-control';
import { PageElement } from 'core/utilities/interface-helpers';
import { useDialog } from 'core/providers/DialogProvider';
import { useAuthState } from 'core/providers/AuthProvider';
import { Role } from 'core/config/roles';
import { TransportJobStatusLabels, TransportStatuses } from 'core/constants/transport-job-status';
import { TransportGroupStatusLabels, TransportGroupStatuses } from 'core/constants/transport-group-status';
import { useTransportCounts } from 'core/hooks/useTransportCounts';
import TransportJobsList from './transport-jobs-list';
import TransportGroupsList from './transport-groups/transport-groups-list';
import ExportTransportJosbDialog from './export-transport-jobs-dialog';

/**
 * Transport: the work that came out of the request queue.
 *
 * Two shapes of the same thing, so two segments of one screen rather than two
 * screens. A run is a sequenced set of jobs given to one driver; a job is one
 * collection and one delivery. Runs used to live behind a button in this
 * screen's header, which meant a run built from the request queue landed
 * somewhere nobody had reason to open — and a run nobody opens is work nobody
 * does.
 *
 * The segment lives in the address, not in state: `/transport/runs` was a real
 * screen people could have bookmarked, and the back button should move between
 * the two the way it moves between anything else.
 */

type TransportTab = 'jobs' | 'batches';

const TransportList = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dialog = useDialog();
  const { userRoles } = useAuthState();
  const [searchParams, setSearchParams] = useSearchParams();
  const counts = useTransportCounts();

  // `/transport/runs` was the old address and may be bookmarked; it lands on the
  // same segment rather than dropping somebody on the jobs list.
  const tab: TransportTab = /\/(batches|runs)$/.test(location.pathname) ? 'batches' : 'jobs';

  /**
   * Each segment offers only its own statuses.
   *
   * A job is pending or collected; a run is assigned or accepted. One list
   * holding both would offer statuses that can never match what is on screen,
   * which reads as a filter that is broken rather than one that found nothing.
   */
  const statusOptions = useMemo<Array<{ value: string; label: string }>>(
    () =>
      tab === 'batches'
        ? TransportGroupStatuses.map((status) => ({ value: status, label: TransportGroupStatusLabels[status] }))
        : TransportStatuses.map((status) => ({ value: status, label: TransportJobStatusLabels[status] })),
    [tab],
  );

  const status = searchParams.get('status') ?? undefined;
  const selected = statusOptions.find((option) => option.value === status);

  const setStatus = (value?: string) =>
    setSearchParams((current) => {
      const next = new URLSearchParams(current);
      if (value) next.set('status', value);
      else next.delete('status');
      return next;
    });

  const changeTab = (next: TransportTab) => {
    // The status goes with the segment it belonged to: carrying "pending" onto
    // the runs list would filter for something no run can be.
    navigate(next === 'batches' ? '/transport/batches' : '/transport');
  };

  const headerActions: PageElement[] = [
    ...([Role.TRANSPORT_ADMIN, Role.SUPER_ADMIN].some((role) => userRoles?.includes(role))
      ? [
          {
            key: 'exportJobs',
            element: (
              <SharedButton
                onClick={() => dialog?.openDialog(<ExportTransportJosbDialog />)}
                type='button'
                appearance='default'
                label='Export jobs'
              />
            ),
          },
        ]
      : []),
  ];

  return (
    <>
      <PageHeader title='Transport' actions={headerActions} />

      <div className='mt-4 flex flex-wrap items-center gap-3'>
        <SharedSegmentedControl<TransportTab>
          ariaLabel='Which transport work to show'
          testIdPrefix='transport-tab'
          value={tab}
          onChange={changeTab}
          // Counting what nobody has yet, not what is in the table: jobs still
          // in the pool, and batches no driver has accepted.
          options={[
            { key: 'jobs', label: 'Jobs', count: counts.jobs.count, countLabel: counts.jobs.label },
            { key: 'batches', label: 'Batches', count: counts.batches.count, countLabel: counts.batches.label },
          ]}
        />

        <div className='w-64' data-testid='transport-status-filter'>
          <Select
            inputId='transport-status'
            isClearable
            isSearchable={false}
            spacing='compact'
            aria-label='Narrow to one status'
            placeholder={tab === 'batches' ? 'Batch status' : 'Job status'}
            options={statusOptions}
            value={selected ?? null}
            onChange={(option) => setStatus(option?.value)}
          />
        </div>
      </div>

      {tab === 'batches' ? <TransportGroupsList status={status} /> : <TransportJobsList status={status} />}
    </>
  );
};

export default TransportList;
